import React from 'react';

import { Tooltip } from 'antd-v5';
import { MdEditNote } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'scouting-ui/lib';

import type { CounselorYouth } from '@appTypes/esb';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { goToMBCBulkEntryRoute } from '@modules/meritBadgeCounselors/mbcBulkEntry/duck/actions';
import { getStartedVersionIds } from '@modules/meritBadgeCounselors/mbcBulkEntry/duck/preselect';

import { mbcProfileMIDSel } from '../duck/selectors';

interface RecordProgressButtonProps {
  meritBadgeId: number;
  selectedYouth: CounselorYouth[];
  noSelectionMessageId: string;
}

/**
 * Opens Bulk Entry preselected with the MB and youth. Disabled when the youth
 * started the MB in different versions, since Bulk Entry would drop some of them
 */
export default function RecordProgressButton({
  meritBadgeId,
  selectedYouth,
  noSelectionMessageId,
}: Readonly<RecordProgressButtonProps>) {
  const dispatch = useDispatch();
  const counselorMemberId = useSelector(mbcProfileMIDSel);

  if (!featureFlags.getFlag('SBL_7033_MBC_CLASS_RECORD_PROGRESS')) return null;

  const hasVersionMismatch =
    getStartedVersionIds(meritBadgeId, selectedYouth).length > 1;

  let tooltip: React.ReactNode = null;
  if (selectedYouth.length === 0) {
    tooltip = <FormattedMessage id={noSelectionMessageId} />;
  } else if (hasVersionMismatch) {
    tooltip = (
      <FormattedMessage id="mbcDashboard.classTable.recordProgress.versionMismatch" />
    );
  }

  return (
    <Tooltip title={tooltip}>
      <Button
        icon={<MdEditNote />}
        type="link"
        size="small"
        disabled={selectedYouth.length === 0 || hasVersionMismatch}
        onClick={() => {
          if (!counselorMemberId) return;

          dispatch(
            goToMBCBulkEntryRoute({
              counselorMemberId,
              meritBadgeId,
              memberIds: selectedYouth.map(youth => youth.memberId),
            }),
          );
        }}
      >
        <FormattedMessage id="mbcDashboard.classTable.recordProgress" />
      </Button>
    </Tooltip>
  );
}
